import mainStyles from './date-picker.scss?inline';
import { Calendar } from '../calendar/calendar.component';
import '../sales-sites';
import type { SalesSites } from '../sales-sites/sales-sites.component';
import { localized, msg, updateLocale } from '../../localization/config';
import { LocaleCode } from '../../types/lang.types';
import { DEFAULT_OPENING_HOURS } from '../../opening-hours.defaults';
import { css, html, LitElement, unsafeCSS } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
@localized()
export class DatePicker extends LitElement {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    @property({ type: String })
    public lang = LocaleCode.DE_CH;

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ type: Number, attribute: 'booking-day-offset' })
    bookingDayOffset = 3;

    @property({ type: Array })
    openingWeekdays: number[] = [];

    @property({ type: Array })
    specialSelectableDates: string[] = [];

    @property({ type: String, attribute: 'opportunity-owning-team' })
    opportunityOwningTeam = '';

    @state()
    protected _isSalesSiteRequired = true;

    @query('der-tm-appointment-booking-calendar') calendar!: Calendar;
    @query('form') formEl!: HTMLFormElement;
    @query('input[name="OpportunityConsultationDate"]') dateInput!: HTMLInputElement;
    @query('der-tm-appointment-booking-sales-sites') salesSitesComponent?: SalesSites;

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        await updateLocale(this.lang ?? LocaleCode.DE_CH);
    }

    override async updated(changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        if (changedProperties.has('lang')) {
            await updateLocale(this.lang ?? LocaleCode.DE_CH);
        }
    }

    /**
     * Resets the date picker component to its initial state.
     * Clears the date input, resets the calendar, and resets the form.
     */
    reset(): void {
        this.dateInput.value = '';
        this.calendar.reset();
        this.formEl.reset();
    }

    /**
     * Validates the date picker form.
     * Validates the sales-sites dropdown first so the browser shows validation feedback.
     * The dropdown has an error-message attribute that displays custom error UI,
     * providing consistent feedback across all devices.
     * @returns True if the form is valid, false otherwise
     */
    validate(): boolean {
        if (this._isSalesSiteRequired && this.salesSitesComponent) {
            const dropdownEl = this.salesSitesComponent.shadowRoot?.querySelector('der-ui-dropdown-searchable') as
                | (HTMLElement & {
                      checkValidity?: () => boolean;
                      reportValidity?: () => boolean;
                  })
                | null;

            const ok = dropdownEl?.checkValidity?.() ?? true;
            if (!ok) {
                dropdownEl?.scrollIntoView?.({ block: 'center', behavior: 'auto' });
                window.setTimeout(() => {
                    dropdownEl?.reportValidity?.();
                }, 300);
                return false;
            }
        }
        if (!this.formEl.checkValidity()) {
            const invalidEl = this.formEl.querySelector(':invalid') as HTMLElement | null;
            invalidEl?.scrollIntoView({ block: 'center', behavior: 'auto' });
            window.setTimeout(() => {
                this.formEl.reportValidity();
            }, 300);
            return false;
        }
        return true;
    }

    /**
     * Gets the form values as a record of form data entries.
     * @returns Record containing form field names and their values
     */
    getFormValue(): Record<string, FormDataEntryValue> {
        return Object.fromEntries(new FormData(this.formEl));
    }

    /**
     * Handles calendar date selection change events.
     * Updates the date input value and dispatches a calendar-date-selected event.
     * @param e CustomEvent containing the selected date
     */
    private _handleCalendarChange(e: CustomEvent) {
        e.stopPropagation();
        this.dateInput.value = e.detail?.date;
        /**
         * Event dispatch
         */
        this.dispatchEvent(
            new CustomEvent('calendar-date-selected', {
                detail: { value: this.dateInput.value },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Handles sales site selection change events.
     * Resets the calendar when a new sales site is selected.
     */
    private _handleSalesSiteChanged(): void {
        this.calendar.reset();
    }

    /**
     * Handles tablist change events for consultation channel selection.
     * Prevents event propagation.
     * @param e CustomEvent from the tablist component
     */
    private _handleTablistChange(e: CustomEvent): void {
        e.stopPropagation();
        this._isSalesSiteRequired = e.detail?.value === this.tabListOptions[0].value;
        const hasSelectedSalesSite = !!this.salesSitesComponent?.selectedTeamId;
        if (hasSelectedSalesSite) {
            this.salesSitesComponent?.resetSelection();
        }
        this.calendar.reset();

        this.dispatchEvent(
            new CustomEvent('consultation-channel-changed', {
                detail: {
                    value: e.detail?.value,
                    isSalesSiteRequired: this._isSalesSiteRequired,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Gets the tab list options for consultation channel selection.
     * Maps consultation channel types to their display values.
     * Note: Values are mapped to language codes in the azure function before sending to CRM.
     * @returns Array of tab options with value, label, and title
     */
    get tabListOptions(): Array<{ value: string; label: string; title: string }> {
        return [
            {
                value: '948150000',
                label: `${msg('tabList.typeOffice')}`,
                title: `${msg('tabList.typeOffice')}`,
            },
            {
                value: '948150001',
                label: `${msg('tabList.typePhone')}`,
                title: `${msg('tabList.typePhone')}`,
            },
            {
                value: '948150002',
                label: `${msg('tabList.typeTeams')}`,
                title: `${msg('tabList.typeTeams')}`,
            },
        ];
    }

    private get _calendarOpeningWeekdays(): number[] {
        if (this.openingWeekdays.length > 0) {
            return this.openingWeekdays;
        }

        return DEFAULT_OPENING_HOURS.defaultOpeningHours.map((entry) => entry.weekday);
    }

    private get _calendarSpecialSelectableDates(): string[] {
        return this._isSalesSiteRequired ? this.specialSelectableDates : [];
    }

    override render() {
        return html`
            <form>
                <div class="m-appointment-booking__subitem-wrapper">
                    <div class="headline">${msg('headlines.consultationChannel')}</div>
                    <der-ui-tablist
                        value="948150000"
                        name="OpportunityConsultationChannel"
                        variant="off-default"
                        .required=${true}
                        .options=${this.tabListOptions}
                        @der-tablist-change=${this._handleTablistChange}
                    ></der-ui-tablist>
                </div>
                ${
                    this.opportunityOwningTeam
                        ? ''
                        : html`
                              <div>
                                  <der-tm-appointment-booking-sales-sites
                                      lang="${this.lang}"
                                      brand="${this.brand}"
                                      tenant="${this.tenant}"
                                      name="OpportunityOwningTeam"
                                      opportunity-owning-team="${this.opportunityOwningTeam}"
                                      label="${msg('headlines.sales-sites')}"
                                      placeholder="${msg('saleSite.placeholder')}"
                                      ?required=${this._isSalesSiteRequired}
                                      @sales-site-selected=${this._handleSalesSiteChanged}
                                  ></der-tm-appointment-booking-sales-sites>
                              </div>
                          `
                }
                <div class="m-appointment-booking__subitem-wrapper">
                    <div class="headline">${msg('headlines.datepicker')}</div>
                    ${keyed(
                        this._isSalesSiteRequired ? 'office' : 'remote',
                        html`<der-tm-appointment-booking-calendar
                            lang=${this.lang}
                            selected-date=${this.dateInput?.value}
                            booking-day-offset=${this.bookingDayOffset}
                            ?month-selection-dropdown=${true}
                            .openingWeekdays=${this._calendarOpeningWeekdays}
                            .specialSelectableDates=${this._calendarSpecialSelectableDates}
                            @der-calendar-change=${this._handleCalendarChange}
                        ></der-tm-appointment-booking-calendar>`
                    )}
                    <div class="invisible-input-wrapper">
                        <input type="text" name="OpportunityConsultationDate" required />
                    </div>
                </div>
            </form>
        `;
    }
}
